// src/utils/analisisJobs.js
// Análisis en segundo plano: el navegador entrega archivos y parámetros una
// sola vez (POST /analisis/jobs) y el servidor hace el resto aunque se
// cierre la pestaña: procesa, genera el PDF y lo envía por correo.
import axios from 'axios';
import { API_BASE_URL } from './config';

// El servidor cambia esta marca por el canal del trabajo en los campos
// session_id: así los eventos del GIS llegan al trabajo y, por él, a esta
// pestaña mientras siga abierta.
export const CANAL_JOB = '{{CANAL}}';

export const EVENTO_JOB_ACTUALIZADO = 'analisisJob:actualizado';
export const EVENTO_VENTANA_JOB = 'analisisJobs:lanzado';

export const ESTADOS_ACTIVOS = ['en_cola', 'procesando', 'visualizando', 'enviando'];

// Configuración de la pestaña que el Dashboard necesita para volver a
// mostrar el análisis igual (modo, equipo, producto, rangos, props…).
const PREFIJOS_SNAPSHOT = [
    'aplicacionesAereas_',
    'herbicidas_',
    'fertilizacion_',
    'custom_props_',
    'aps_',
];
const CLAVES_SNAPSHOT = ['selectedMapStyle', 'userData'];
const MAX_VALOR_SNAPSHOT = 200 * 1024;

// El token de sesión no viaja con el análisis: el servidor abre el
// Dashboard con su propio token de un solo uso.
const sinDatosDeSesion = (valor) => {
    try {
        const data = JSON.parse(valor || '{}');
        const quitar = (obj) => {
            if (!obj || typeof obj !== 'object') return;
            Object.keys(obj).forEach((k) => {
                if (/token|password|contrase|secret/i.test(k)) delete obj[k];
                else quitar(obj[k]);
            });
        };
        quitar(data);
        return JSON.stringify(data);
    } catch {
        return null;
    }
};

export const tomarSnapshotLocal = () => {
    const snapshot = {};
    try {
        for (let i = 0; i < localStorage.length; i += 1) {
            const clave = localStorage.key(i);
            if (!clave) continue;
            const incluida = CLAVES_SNAPSHOT.includes(clave) || PREFIJOS_SNAPSHOT.some((p) => clave.startsWith(p));
            // El ID de un análisis no se arrastra: reusar uno ya analizado lo borra en el GIS.
            if (!incluida || clave.endsWith('_manual_id')) continue;
            let valor = localStorage.getItem(clave);
            if (clave === 'userData') valor = sinDatosDeSesion(valor);
            if (typeof valor === 'string' && valor.length <= MAX_VALOR_SNAPSHOT) snapshot[clave] = valor;
        }
    } catch {
        // Sin localStorage el análisis corre igual; solo el PDF usa valores por defecto.
    }
    return snapshot;
};

export const aplicarSnapshotLocal = (snapshot = {}, { incluirSesion = false } = {}) => {
    Object.entries(snapshot || {}).forEach(([clave, valor]) => {
        if (!incluirSesion && clave === 'userData') return;
        try {
            localStorage.setItem(clave, valor);
        } catch {
            // continuar con el resto
        }
    });
};

// Nombre y correos extra que el analista escribe al pulsar «Analizar»: los
// toma el siguiente análisis que se lance (cada proceso llama a
// lanzarAnalisisEnSegundoPlano sin tener que pasarlos).
let datosProximoAnalisis = null;
export const fijarDatosProximoAnalisis = (datos) => { datosProximoAnalisis = datos || null; };

export const obtenerPreferenciasAnalisis = async (idUsuario) => {
    const { data } = await axios.get(`${API_BASE_URL}analisis/preferencias/${idUsuario}`);
    return data;
};

export const guardarPreferenciasAnalisis = async (idUsuario, correosExtra) => {
    const { data } = await axios.put(`${API_BASE_URL}analisis/preferencias/${idUsuario}`, { correosExtra });
    return data;
};

/**
 * Lanza un análisis en segundo plano.
 * `pasos` = [{ ruta, campos: {…}, archivos: { campoGis: File|Blob } }]
 * Los Blob deben llevar nombre: se pasan como { blob, nombre }.
 */
export const lanzarAnalisisEnSegundoPlano = async ({
    tipoAnalisis,
    modo = 'NORMAL',
    equipo = null,
    titulo,
    idUsuario,
    idAnalisis,
    sesionOrigen,
    pasos,
    ui = {},
    comentario = '',
    // Recálculo de una capa de un análisis ya hecho (sin PDF, correo ni cobro).
    soloCapa = false,
}) => {
    const formData = new FormData();
    let indice = 0;
    const pasosSpec = pasos.map((paso) => {
        const archivos = {};
        Object.entries(paso.archivos || {}).forEach(([campoGis, archivo]) => {
            if (!archivo) return;
            const campoSubida = `archivo_${indice}`;
            indice += 1;
            if (archivo.blob) formData.append(campoSubida, archivo.blob, archivo.nombre || `${campoSubida}.bin`);
            else formData.append(campoSubida, archivo, archivo.name || `${campoSubida}.bin`);
            archivos[campoGis] = campoSubida;
        });
        return { ruta: paso.ruta, campos: paso.campos || {}, archivos };
    });

    const datos = soloCapa ? null : datosProximoAnalisis;
    if (!soloCapa) datosProximoAnalisis = null;

    formData.append('spec', JSON.stringify({
        tipoAnalisis,
        modo,
        equipo,
        titulo,
        idUsuario,
        idAnalisis: String(idAnalisis),
        sesionOrigen,
        pasos: pasosSpec,
        snapshot: tomarSnapshotLocal(),
        ui,
        comentario,
        soloCapa,
        nombre: datos?.nombre || '',
        correosExtra: datos?.correosExtra || [],
    }));

    const response = await axios.post(`${API_BASE_URL}analisis/jobs`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
    const job = response?.data?.job || null;
    if (soloCapa) return response.data;
    // Un análisis nuevo: recargar ya no debe abrir el anterior.
    quitarJobDeUrl();
    if (job) window.dispatchEvent(new CustomEvent(EVENTO_VENTANA_JOB, { detail: job }));
    return response.data;
};

export const obtenerJob = async (jobId, idUsuario) => {
    const { data } = await axios.get(`${API_BASE_URL}analisis/jobs/${jobId}`, { params: { idUsuario } });
    return data?.job || null;
};

// Espera a que un trabajo termine: escucha el aviso por socket y, por si se
// pierde, consulta cada pocos segundos. Resuelve con el resumen final.
export const esperarJob = (jobId, { idUsuario, socket, intervaloMs = 4000, limiteMs = 30 * 60 * 1000 } = {}) => new Promise((resolve, reject) => {
    let terminado = false;
    const inicio = Date.now();
    let temporizador = null;
    const cerrar = (fn, valor) => {
        if (terminado) return;
        terminado = true;
        clearTimeout(temporizador);
        if (socket?.off) socket.off(EVENTO_JOB_ACTUALIZADO, onActualizado);
        fn(valor);
    };
    const revisar = (job) => {
        if (!job || job.id !== String(jobId)) return;
        if (job.estado === 'terminado') cerrar(resolve, job);
        else if (job.estado === 'error') cerrar(reject, new Error(job.error?.mensaje || job.mensaje || 'No se pudo completar.'));
    };
    function onActualizado(job) { revisar(job); }
    const consultar = async () => {
        if (terminado) return;
        if (Date.now() - inicio > limiteMs) {
            cerrar(reject, new Error('El servidor tardó demasiado en responder.'));
            return;
        }
        try {
            revisar(await obtenerJob(jobId, idUsuario));
        } catch {
            // se reintenta en la siguiente vuelta
        }
        if (!terminado) temporizador = setTimeout(consultar, intervaloMs);
    };
    if (socket?.on) socket.on(EVENTO_JOB_ACTUALIZADO, onActualizado);
    temporizador = setTimeout(consultar, intervaloMs);
});

export const listarAnalisisJobs = async (idUsuario, { activos = false, limite = 15 } = {}) => {
    const { data } = await axios.get(`${API_BASE_URL}analisis/jobs`, {
        params: { idUsuario, activos: activos ? 1 : undefined, limite },
    });
    return Array.isArray(data?.jobs) ? data.jobs : [];
};

export const obtenerContextoJob = async (jobId, { idUsuario, token } = {}) => {
    const { data } = await axios.get(`${API_BASE_URL}analisis/jobs/${jobId}/contexto`, {
        params: token ? { t: token } : { idUsuario },
    });
    return data;
};

export const actualizarSesionJob = (jobId, idUsuario, sessionID) => axios
    .post(`${API_BASE_URL}analisis/jobs/${jobId}/sesion`, { idUsuario, sessionID })
    .catch(() => null);

export const marcarJobVisto = (jobId, idUsuario) => axios
    .post(`${API_BASE_URL}analisis/jobs/${jobId}/visto`, { idUsuario })
    .catch(() => null);

export const reintentarJob = async (jobId, idUsuario) => {
    const { data } = await axios.post(`${API_BASE_URL}analisis/jobs/${jobId}/reintentar`, { idUsuario });
    return data?.job || null;
};

export const enviarReporteJob = async (jobId, token, payload) => {
    const { data } = await axios.post(`${API_BASE_URL}analisis/jobs/${jobId}/reporte`, payload, {
        params: { t: token },
    });
    return data;
};

// La URL de la pestaña apunta al trabajo que terminó en ella: al recargar,
// /mapeo?analisisJob=<id> lo vuelve a mostrar en vez de dejar el mapa vacío.
// replaceState no avisa al router, así que no se vuelve a montar el Dashboard.
const PARAM_JOB = 'analisisJob';
const reemplazarUrl = (url) => window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);

export const fijarJobEnUrl = (canal) => {
    const [, jobId] = String(canal || '').match(/^job_([a-f0-9]{24})$/i) || [];
    if (!jobId) return;
    const url = new URL(window.location.href);
    if (url.searchParams.get(PARAM_JOB) === jobId) return;
    url.searchParams.set(PARAM_JOB, jobId);
    reemplazarUrl(url);
};

const quitarJobDeUrl = () => {
    const url = new URL(window.location.href);
    if (!url.searchParams.has(PARAM_JOB)) return;
    url.searchParams.delete(PARAM_JOB);
    reemplazarUrl(url);
};

export const urlDescargaReporte =(reporteId) => `${API_BASE_URL}reporteria/reportes/${reporteId}/download`;

// Título legible para la lista y el correo.
const NOMBRES = {
    APLICACIONES_AEREAS: 'Aplicaciones Aéreas',
    HERBICIDAS: 'Herbicidas',
    FERTILIZACION: 'Fertilización',
    COSECHA_MECANICA: 'Cosecha Mecánica',
};
const EQUIPOS = {
    DRONE: 'Dron',
    HELICOPTERO: 'Helicóptero',
    AVIONETA: 'Avioneta',
    AUTOPROPULSADO: 'Autopropulsado',
};
export const tituloAnalisis = (tipoAnalisis, { equipo, modo } = {}) => [
    NOMBRES[tipoAnalisis] || tipoAnalisis,
    equipo ? (EQUIPOS[String(equipo).toUpperCase()] || equipo) : null,
    modo ? (modo === 'VRA' ? 'VRA' : 'Normal') : null,
].filter(Boolean).join(' · ');
